const BASE = process.env.REACT_APP_API_URL || ''

/**
 * Get document id from a record (supports both _id and id for paginated/API responses).
 */
export const getDocumentId = (doc: { _id?: string; id?: string } | undefined | null): string =>
  doc ? (doc._id ?? doc.id ?? '') : ''

/**
 * Extract the stored filename from an attachment url (last segment).
 * Handles both forward slashes and Windows backslashes.
 */
export const getAttachmentFilename = (url: string | undefined | null): string => {
  if (url == null || url === '') return ''
  const trimmed = String(url).trim()
  const parts = trimmed.split(/[/\\]/).filter(Boolean)
  return parts.length > 0 ? parts[parts.length - 1] : ''
}

// Media routes: use /api/ prefix if backend serves exercices/remarques under /api (same as other routes).
// Set REACT_APP_MEDIA_PATH_PREFIX="" in .env if your backend serves at /remarques/ and /exercices/ (no /api).
const MEDIA_PREFIX = (process.env.REACT_APP_MEDIA_PATH_PREFIX ?? '/api').replace(/^\/|\/$/g, '')

/**
 * Build the view URL for an exercice attachment.
 * API expects: GET /api/exercices/<exerciceId>/<filename> (or /exercices/... if backend mounts at root).
 * Document _id in URL, not teacherId.
 */
type AttachmentLike = { url?: string; path?: string; fileName?: string }

export const getExerciceAttachmentUrl = (
  exerciceId: string | undefined,
  attachment: AttachmentLike
): string => {
  if (!exerciceId || !attachment) return ''
  const urlOrPath = attachment.url ?? (attachment as { path?: string }).path ?? ''
  const filename = getAttachmentFilename(urlOrPath) || (attachment.fileName ?? '').trim()
  if (!filename) return ''
  const base = BASE.endsWith('/') ? BASE.slice(0, -1) : BASE
  const path = MEDIA_PREFIX ? `${MEDIA_PREFIX}/exercices` : 'exercices'
  return `${base}/${path}/${exerciceId}/${encodeURIComponent(filename)}`
}

/**
 * Build the view URL for a remarque attachment.
 * API expects: GET /api/remarques/<remarqueId>/<filename> (or /remarques/... if backend mounts at root).
 * Document _id in URL, not teacherId.
 */
export const getRemarqueAttachmentUrl = (
  remarqueId: string | undefined,
  attachment: AttachmentLike
): string => {
  if (!remarqueId || !attachment) return ''
  const urlOrPath = attachment.url ?? (attachment as { path?: string }).path ?? ''
  const filename = getAttachmentFilename(urlOrPath) || (attachment.fileName ?? '').trim()
  if (!filename) return ''
  const base = BASE.endsWith('/') ? BASE.slice(0, -1) : BASE
  const path = MEDIA_PREFIX ? `${MEDIA_PREFIX}/remarques` : 'remarques'
  return `${base}/${path}/${remarqueId}/${encodeURIComponent(filename)}`
}

/**
 * Build full URL for an attachment path (remarque, exercice, etc.).
 * - undefined/null/empty → ''
 * - already absolute (http/https) → return as-is
 * - relative path → BASE + path (adds / if path does not start with /)
 * Note: For exercice/remarque images, use getExerciceAttachmentUrl / getRemarqueAttachmentUrl
 * so the URL uses document _id (required by the API).
 */
export const getFilePath = (path: string | undefined | null): string => {
  if (path == null || path === '') return ''
  const trimmed = String(path).trim()
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed
  const base = BASE.endsWith('/') ? BASE.slice(0, -1) : BASE
  const segment = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return base + segment
}
